(() => {
  const formatMm = value => `${Number.isInteger(value) ? value : value.toFixed(1)} mm`;
  const normalRounding = value => Math.round(value);
  const nextEvenAfterRounding = value => {
    const rounded = Math.round(value);
    return rounded % 2 === 0 ? rounded : rounded + 1;
  };

  function supportCount(length) {
    if (length >= 3500) return 2;
    if (length >= 2500) return 1;
    return 0;
  }

  function operatingLength(height) {
    return height <= 2000 ? height - 300 : height - 1000;
  }

  function calculate(system, length, height, fabric, shortSide = null) {
    const spacing = fabric === 'wide' ? 100 : 90;
    const fabricLabel = fabric === 'wide' ? 'Wide fabric · 100 mm' : 'Narrow fabric · 90 mm';
    const fabricHeight = height - 65;
    const operation = operatingLength(height);
    const items = [];
    let title = '';

    if (system === 'complete-chain') {
      title = 'Chain Operated Tracks';
      const cutTrack = length - 25;
      items.push(
        ['Track cut length', formatMm(cutTrack), 'L − 25 mm'],
        ['Aluminium core', formatMm(length - 40), 'L − 40 mm'],
        ['Runners', `${normalRounding(length / spacing)} pcs`, fabricLabel],
        ['White pull rope', formatMm(cutTrack * 2 + operation * 2), height <= 2000 ? '(L − 25) × 2 + (H − 300) × 2' : '(L − 25) × 2 + (H − 1000) × 2'],
        ['Chain length', formatMm(operation * 2), height <= 2000 ? '(H − 300) × 2' : '(H − 1000) × 2'],
        ['Fabric cut height', formatMm(fabricHeight), 'H − 65 mm'],
        ['Centre supports', `${supportCount(length)} pcs`, 'Based on complete track length']
      );
    }

    if (system === 'connected-chain') {
      title = 'Chain Operated Connected';
      const piece = (length - 25) / 2;
      const runners = nextEvenAfterRounding(length / spacing);
      items.push(
        ['Track pieces', `2 × ${formatMm(piece)}`, '(L − 25 mm) ÷ 2'],
        ['Top aluminium core', formatMm(piece - 10), 'One track piece − 10 mm'],
        ['Bottom aluminium core', formatMm(piece - 20), 'One track piece − 20 mm'],
        ['Runners', `${runners} pcs`, `${fabricLabel} · even quantity`],
        ['White pull rope', formatMm((length - 30) * 2 + operation * 2), height <= 2000 ? '(L − 30) × 2 + (H − 300) × 2' : '(L − 30) × 2 + (H − 1000) × 2'],
        ['Chain length', formatMm(operation * 2), height <= 2000 ? '(H − 300) × 2' : '(H − 1000) × 2'],
        ['Fabric cut height', formatMm(fabricHeight), 'H − 65 mm'],
        ['Carrier', '1 pc', 'Connected-track component'],
        ['Centre supports', `${supportCount(piece)} pcs`, 'Based on one individual track piece']
      );
    }

    if (system === 'straight-wand') {
      title = 'Wand Operated Tracks';
      items.push(
        ['Track cut length', formatMm(length - 25), 'L − 25 mm'],
        ['Aluminium core', formatMm(length - 35), 'L − 35 mm'],
        ['Runners', `${normalRounding(length / spacing)} pcs`, fabricLabel],
        ['Wand length', formatMm(operation), height <= 2000 ? 'H − 300 mm' : 'H − 1000 mm'],
        ['Fabric cut height', formatMm(fabricHeight), 'H − 65 mm'],
        ['Centre supports', `${supportCount(length)} pcs`, 'Based on complete track length']
      );
    }

    if (system === 'l-shape-wand') {
      title = 'L-Shape Window Wand Operated Tracks';
      const longTrack = length - 20;
      const shortTrack = shortSide - 120;
      items.push(
        ['Long-side track', formatMm(longTrack), 'Long-side width − 20 mm'],
        ['Long-side aluminium core', formatMm(longTrack - 40), 'Long-side track − 40 mm'],
        ['Long-side runners', `${normalRounding(longTrack / spacing)} pcs`, `${fabricLabel} · individual track`],
        ['Long-side wand', formatMm(operation), height <= 2000 ? 'H − 300 mm' : 'H − 1000 mm'],
        ['Long-side centre supports', `${supportCount(longTrack)} pcs`, 'Based on long-side track'],
        ['Short-side track', formatMm(shortTrack), 'Short-side width − 120 mm'],
        ['Short-side aluminium core', formatMm(shortTrack - 40), 'Short-side track − 40 mm'],
        ['Short-side runners', `${normalRounding(shortTrack / spacing)} pcs`, `${fabricLabel} · individual track`],
        ['Short-side wand', formatMm(operation), height <= 2000 ? 'H − 300 mm' : 'H − 1000 mm'],
        ['Short-side centre supports', `${supportCount(shortTrack)} pcs`, 'Based on short-side track'],
        ['Fabric cut height', formatMm(fabricHeight), 'H − 65 mm']
      );
    }

    if (system === 'automatic-complete') {
      title = 'Automatic Operated Tracks';
      const track = length - 85;
      const pvcRequired = length >= 2800;
      items.push(
        ['Track cut length', formatMm(track), 'L − 85 mm'],
        ['Aluminium core', formatMm(length - 53), 'L − 53 mm'],
        ['Belt length', formatMm(track * 2 + 100), '(L − 85 mm) × 2 + 100 mm'],
        ['Runners', `${normalRounding(length / (fabric === 'wide' ? 100 : 85))} pcs`, fabric === 'wide' ? 'Wide fabric · L ÷ 100 mm' : 'Narrow fabric · L ÷ 85 mm'],
        ['PVC strip', pvcRequired ? `1 × ${formatMm(track)}` : '0 pcs', pvcRequired ? 'Required when L ≥ 2800 mm' : 'Not required below 2800 mm'],
        ['Fabric cut height', formatMm(height - 75), 'H − 75 mm']
      );
    }

    if (system === 'automatic-connected') {
      title = 'Automatic Operated Connected';
      const piece = (length - 160) / 2;
      const autoSpacing = fabric === 'wide' ? 100 : 85;
      const pvcRequired = length >= 2800;
      items.push(
        ['Track pieces', `2 × ${formatMm(piece)}`, '(L − 160 mm) ÷ 2'],
        ['First aluminium core', formatMm(piece + 54), 'One track piece + 54 mm'],
        ['Second aluminium core', formatMm(piece + 47), 'One track piece + 47 mm'],
        ['Belt length', formatMm((length - 85) * 2 + 100), '(L − 85 mm) × 2 + 100 mm'],
        ['Runners', `${nextEvenAfterRounding(length / autoSpacing)} pcs`, `${fabric === 'wide' ? 'Wide · L ÷ 100 mm' : 'Narrow · L ÷ 85 mm'} · even quantity`],
        ['PVC strips', pvcRequired ? `2 × ${formatMm(piece)}` : '0 pcs', pvcRequired ? 'Required when L ≥ 2800 mm' : 'Not required below 2800 mm'],
        ['Fabric cut height', formatMm(height - 75), 'H − 75 mm']
      );
    }

    return { title, items };
  }

  function pdfEscape(value) {
    return String(value).replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)').replace(/[^\x20-\x7E]/g, '-');
  }

  function downloadProductionPdf(output, length, height, fabric, shortSide = null) {
    const lines = [];
    const text = (x, y, size, value, bold = false) => {
      lines.push(`BT /${bold ? 'F2' : 'F1'} ${size} Tf ${x} ${y} Td (${pdfEscape(value)}) Tj ET`);
    };
    lines.push('0.66 0.48 0.18 rg');
    lines.push('50 786 495 4 re f');
    lines.push('0 0 0 rg');
    text(50, 755, 10, 'LUXWORLD SMART PRODUCTION INTELLIGENCE', true);
    text(50, 730, 22, 'Dream Blinds - Manual Track', true);
    text(50, 708, 13, output.title, false);
    text(435, 756, 9, new Intl.DateTimeFormat('en-AU').format(new Date()));
    lines.push('0.90 0.89 0.85 RG 50 690 m 545 690 l S');
    text(50, 670, 9, shortSide ? 'LONG-SIDE WIDTH' : 'ENTERED WIDTH', false);
    text(165, 670, 11, formatMm(length), true);
    text(300, 670, 9, 'ENTERED HEIGHT', false);
    text(415, 670, 11, formatMm(height), true);
    text(50, 648, 9, shortSide ? 'SHORT-SIDE WIDTH' : 'FABRIC', false);
    text(165, 648, 11, shortSide ? formatMm(shortSide) : (fabric === 'wide' ? 'Wide - 100 mm spacing' : 'Narrow - 90 mm spacing'), true);
    if (shortSide) {
      text(300, 648, 9, 'FABRIC', false);
      text(415, 648, 9, fabric === 'wide' ? 'Wide - 100 mm' : 'Narrow - 90 mm', true);
    }
    lines.push('0.10 0.10 0.09 rg 50 610 495 28 re f');
    lines.push('1 1 1 rg');
    text(62, 619, 9, 'PRODUCTION ITEM', true);
    text(390, 619, 9, 'REQUIRED', true);
    lines.push('0 0 0 rg');
    let y = 586;
    const rowStep = output.items.length > 9 ? 39 : 48;
    output.items.forEach(([name, value, formula]) => {
      text(62, y, 10, name, true);
      text(62, y - 13, 7, formula, false);
      text(390, y - 2, 11, value, true);
      lines.push(`0.90 0.89 0.85 RG 50 ${y - 23} m 545 ${y - 23} l S 0 0 0 RG`);
      y -= rowStep;
    });
    text(50, 80, 8, 'All dimensions are production cut measurements in millimetres.', false);
    text(50, 67, 8, 'Verify the entered measurements before cutting.', true);
    text(430, 67, 8, 'LUXWORLD INTERNAL', false);

    const stream = lines.join('\n');
    const objects = [
      '<< /Type /Catalog /Pages 2 0 R >>',
      '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
      '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 5 0 R /F2 6 0 R >> >> /Contents 4 0 R >>',
      `<< /Length ${new TextEncoder().encode(stream).length} >>\nstream\n${stream}\nendstream`,
      '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
      '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>'
    ];
    let pdf = '%PDF-1.4\n';
    const offsets = [0];
    objects.forEach((object, index) => {
      offsets.push(new TextEncoder().encode(pdf).length);
      pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
    });
    const xref = new TextEncoder().encode(pdf).length;
    pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
    offsets.slice(1).forEach(offset => { pdf += `${String(offset).padStart(10, '0')} 00000 n \n`; });
    pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
    const blob = new Blob([new TextEncoder().encode(pdf)], { type: 'application/pdf' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    const dimensions = shortSide ? `${Math.round(length)}-${Math.round(shortSide)}x${Math.round(height)}` : `${Math.round(length)}x${Math.round(height)}`;
    link.download = `LUXWORLD-Dream-Blinds-${dimensions}-${new Date().toISOString().slice(0, 10)}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  }

  document.querySelectorAll('[data-lux-dream-manual]').forEach(root => {
    const form = root.querySelector('[data-calculator-form]');
    const empty = root.querySelector('[data-empty-state]');
    const results = root.querySelector('[data-results]');
    const error = root.querySelector('[data-error]');

    form.querySelectorAll('input[name="system"]').forEach(input => {
      input.addEventListener('change', () => {
        const isLShape = input.checked && input.value === 'l-shape-wand';
        if (!input.checked) return;
        root.querySelector('[data-straight-width]').hidden = isLShape;
        root.querySelector('input[name="trackLength"]').required = !isLShape;
        root.querySelectorAll('[data-l-shape-width]').forEach(field => { field.hidden = !isLShape; });
        root.querySelectorAll('input[name="longSide"], input[name="shortSide"]').forEach(field => { field.required = isLShape; });
        const isAutomatic = input.value === 'automatic-complete' || input.value === 'automatic-connected';
        root.querySelector('[data-narrow-spacing]').textContent = isAutomatic ? '85 mm spacing' : '90 mm spacing';
      });
    });

    form.addEventListener('submit', event => {
      event.preventDefault();
      const data = new FormData(form);
      const system = data.get('system');
      const isLShape = system === 'l-shape-wand';
      const length = Number(data.get(isLShape ? 'longSide' : 'trackLength'));
      const shortSide = isLShape ? Number(data.get('shortSide')) : null;
      const height = Number(data.get('blindHeight'));
      if (!Number.isFinite(length) || !Number.isFinite(height) || length <= 0 || height <= 0 || (isLShape && (!Number.isFinite(shortSide) || shortSide <= 120))) {
        error.textContent = isLShape ? 'Enter valid long-side, short-side and height measurements.' : 'Enter a valid track width and blind height.';
        error.hidden = false;
        return;
      }
      if (height <= 1000) {
        error.textContent = 'The entered height is too short for the locked operating-length formula. Please verify the measurement.';
        error.hidden = false;
        return;
      }
      error.hidden = true;
      const output = calculate(system, length, height, data.get('fabric'), shortSide);
      root.querySelector('[data-result-title]').textContent = output.title;
      root.querySelector('[data-result-date]').textContent = new Intl.DateTimeFormat('en-AU', { dateStyle: 'medium' }).format(new Date());
      const meta = [
        [isLShape ? 'Long-side width' : 'Entered track width', formatMm(length)],
        ['Entered blind height', formatMm(height)],
        ['Fabric', data.get('fabric') === 'wide' ? 'Wide' : 'Narrow']
      ];
      if (isLShape) meta.splice(1, 0, ['Short-side width', formatMm(shortSide)]);
      root.querySelector('[data-result-meta]').innerHTML = meta.map(([k, v]) => `<div><dt>${k}</dt><dd>${v}</dd></div>`).join('');
      root.querySelector('[data-result-list]').innerHTML = output.items.map(([name, value, formula]) => `<div class="lux-dream__result"><div><span>${name}</span><small>${formula}</small></div><strong>${value}</strong></div>`).join('');
      empty.hidden = true;
      results.hidden = false;
      root.querySelector('[data-production-sheet]').classList.remove('lux-dream__sheet--ready');
      requestAnimationFrame(() => root.querySelector('[data-production-sheet]').classList.add('lux-dream__sheet--ready'));
      root._latestProduction = { output, length, height, fabric: data.get('fabric'), shortSide };
      downloadProductionPdf(output, length, height, data.get('fabric'), shortSide);
      if (window.matchMedia('(max-width: 900px)').matches) results.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    root.querySelector('[data-pdf]').addEventListener('click', () => {
      const latest = root._latestProduction;
      if (latest) downloadProductionPdf(latest.output, latest.length, latest.height, latest.fabric, latest.shortSide);
    });
  });
})();
